import {
  Button,
  Card,
  Col,
  ConfigProvider,
  DatePicker,
  Form,
  Icons,
  Input,
  List,
  message,
  Row,
  Space,
  Table,
  TextField,
  useTable,
} from "@pankod/refine-antd";
import {
  HttpError,
  IResourceComponentsProps,
  LogicalFilter,
} from "@pankod/refine-core";
import { DefaultMaskedInput } from "components/extra/default-masked-input";
import dayjs, { Dayjs } from "dayjs";
import { STATUS_SIGO_LIST } from "enums/sigoStatus";
import { ADMIN_BFF_URL } from "environment";
import { IBudgetIndicated } from "interfaces";
import { useState } from "react";
import { axiosInstance } from "services/axios";
import { formatCPF } from "utils/formaters";

interface IBudgetIndicatedFilterInput {
  control_number: string | null;
  affiliate_code: string | null;
  affiliate_cpf: string | null;
  affiliate_phone: string | null;
  created_range: [Dayjs, Dayjs] | null;
  payment_range: [Dayjs, Dayjs] | null;
}

type IBudgetIndicatedFilterOutput = Omit<
  IBudgetIndicatedFilterInput,
  "created_range" | "payment_range"
> & {
  budget_created_at_from: string | null;
  budget_created_at_to: string | null;
  payment_date_from: string | null;
  payment_date_to: string | null;
};

function normalizeSearchParams(
  params: IBudgetIndicatedFilterInput
): IBudgetIndicatedFilterOutput {
  const [budget_from, budget_to] = params.created_range || [];
  const [payment_from, payment_to] = params.payment_range || [];

  return {
    control_number: params.control_number,
    affiliate_code: params.affiliate_code,
    affiliate_cpf: params.affiliate_cpf,
    affiliate_phone: params.affiliate_phone?.replace(/\D/g, "") || null,
    budget_created_at_from: budget_from
      ? budget_from.format("YYYY-MM-DD 00:00:00")
      : null,
    budget_created_at_to: budget_to
      ? budget_to.format("YYYY-MM-DD 23:59:59")
      : null,
    payment_date_from: payment_from
      ? payment_from.format("YYYY-MM-DD 00:00:00")
      : null,
    payment_date_to: payment_to
      ? payment_to.format("YYYY-MM-DD 23:59:59")
      : null,
  };
}

export const BudgetsIndicatedList: React.FC<IResourceComponentsProps> = () => {
  const [enabled, setEnabled] = useState(false);
  const [isLoading, setLoading] = useState(false);

  const { tableProps, searchFormProps } = useTable<
    IBudgetIndicated,
    HttpError,
    IBudgetIndicatedFilterInput
  >({
    resource: "v1/indications",
    initialCurrent: 1,
    initialPageSize: 10,
    hasPagination: enabled,
    onSearch: (values) => {
      const params = normalizeSearchParams(values);
      const filtros: LogicalFilter[] = Object.entries(params).map(
        ([field, value]) => ({
          field,
          operator: "eq",
          value,
        })
      );

      const hasFilters = filtros.some((f) => f.value);
      setEnabled(hasFilters);

      return filtros;
    },
    queryOptions: { enabled },
  });

  const handleCleanFilters = () => {
    searchFormProps.form?.resetFields();
    setEnabled(false);
  };

  const handleExportCSV = async () => {
    try {
      setLoading(true);
      let allData: IBudgetIndicated[] = [];
      let currentPage = 1;
      let hasMoreData = true;

      const queryParams = new URLSearchParams();
      const filters = searchFormProps.form?.getFieldsValue() || {};
      const normalizedFilters = normalizeSearchParams(
        filters as IBudgetIndicatedFilterInput
      );

      Object.entries(normalizedFilters).forEach(([key, value]) => {
        if (value) {
          queryParams.append(key, String(value));
        }
      });

      while (hasMoreData) {
        const response = await axiosInstance.get(
          `${ADMIN_BFF_URL}/v1/indications/?page=${currentPage}&size=100&${queryParams.toString()}`
        );

        if (response.data?.items?.length > 0) {
          allData = [...allData, ...response.data.items];
          currentPage++;
          hasMoreData = response.data.items.length === 100;
        } else {
          hasMoreData = false;
        }
      }

      const csvRows = [
        [
          "Controle",
          "Código Afiliado",
          "Nome",
          "CPF",
          "Telefone",
          "E-mail",
          "Seguradora de saúde",
          "Status sigo",
          "Data de Criação",
          "Data de Pagamento",
          "Valor do Orçamento",
        ],
        ...allData.map((item: IBudgetIndicated) => [
          item.control_number,
          item.affiliate_code,
          item.affiliate_name,
          formatCPF(item.affiliate_cpf),
          item.affiliate_phone,
          item.affiliate_email,
          item.health_insurer,
          getStatusLabel(Number(item.status_sigo)),
          dayjs(item.budget_created_at).format("DD/MM/YYYY HH:mm"),
          dayjs(item.payment_date).format("DD/MM/YYYY HH:mm"),
          item.budget_amount,
        ]),
      ];

      const csvContent = csvRows.map((e) => e.join(";")).join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `budgets-indicated_${dayjs().format("YYYY-MM-DD_HH-mm")}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("❌ Erro ao exportar CSV:", error);
      message.error("Erro ao exportar CSV. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (value: number) => {
    const status = STATUS_SIGO_LIST.find((item) => item.value === value);
    return status?.label || "-";
  };

  return (
    <List
      headerProps={{
        extra: (
          <Space>
            <Button
              icon={<Icons.DownloadOutlined />}
              onClick={handleExportCSV}
              disabled={!tableProps?.dataSource?.length || isLoading}
              loading={isLoading}
            >
              Exportar CSV
            </Button>
          </Space>
        ),
      }}
    >
      <Card title="Busca">
        <ConfigProvider>
          <Form {...searchFormProps} layout="vertical">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8}>
                <Form.Item name="control_number" label="Controle">
                  <Input placeholder="Digite o número de controle" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="affiliate_code" label="Código Afiliado">
                  <Input placeholder="Digite o código do afiliado" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="affiliate_cpf" label="CPF">
                  <DefaultMaskedInput
                    mask="000.000.000-00"
                    className="input-custom ant-input"
                    placeholder="Digite o CPF"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="affiliate_phone" label="Telefone">
                  <DefaultMaskedInput
                    mask="+55 (00) 00000-0000"
                    className="input-custom ant-input"
                    placeholder="Digite o telefone"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="created_range" label="Criação (de/até)">
                  <DatePicker.RangePicker
                    format="DD/MM/YYYY"
                    style={{ width: "100%" }}
                    placeholder={["Data inicial", "Data final"]}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="payment_range" label="Pagamento (de/até)">
                  <DatePicker.RangePicker
                    format="DD/MM/YYYY"
                    style={{ width: "100%" }}
                    placeholder={["Data inicial", "Data final"]}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} style={{ textAlign: "right", marginTop: 8 }}>
                <Space>
                  <Button
                    type="default"
                    icon={<Icons.ClearOutlined />}
                    onClick={handleCleanFilters}
                  >
                    Limpar
                  </Button>
                  <Button
                    type="primary"
                    icon={<Icons.SearchOutlined />}
                    htmlType="submit"
                  >
                    Buscar
                  </Button>
                </Space>
              </Col>
            </Row>
          </Form>
        </ConfigProvider>
      </Card>

      <Table
        {...tableProps}
        rowKey="id"
        dataSource={
          enabled && !tableProps.loading ? tableProps?.dataSource : []
        }
        loading={enabled ? tableProps.loading : false}
        scroll={{ x: "max-content" }}
        pagination={{
          ...tableProps.pagination,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} de ${total} registros`,
        }}
      >
        <Table.Column
          dataIndex="control_number"
          title="Controle"
          render={(value) => <TextField value={value} />}
          width={120}
        />
        <Table.Column
          dataIndex="affiliate_code"
          title="Código Afiliado"
          render={(value) => <TextField value={value} />}
          width={150}
        />
        <Table.Column
          dataIndex="affiliate_name"
          title="Nome"
          render={(value) => <TextField value={value} />}
          width={200}
        />
        <Table.Column
          dataIndex="affiliate_cpf"
          title="CPF"
          render={(value) => <TextField value={formatCPF(value)} />}
          width={150}
        />
        <Table.Column
          dataIndex="affiliate_phone"
          title="Telefone"
          render={(value) => <TextField value={value} />}
          width={150}
        />
        <Table.Column
          dataIndex="affiliate_email"
          title="E-mail"
          render={(value) => <TextField value={value} />}
          width={200}
        />
        <Table.Column
          dataIndex="health_insurer"
          title="Seguradora de saúde"
          render={(value) => <TextField value={value} />}
          width={180}
        />
        <Table.Column
          dataIndex="status_sigo"
          title="Status sigo"
          render={(value) => (
            <TextField value={getStatusLabel(Number(value))} />
          )}
          width={150}
        />
        <Table.Column
          dataIndex="budget_created_at"
          title="Data de Criação"
          render={(value) => (
            <TextField
              value={value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "-"}
            />
          )}
          width={180}
        />
        <Table.Column
          dataIndex="payment_date"
          title="Data de Pagamento"
          render={(value) => (
            <TextField
              value={value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "-"}
            />
          )}
          width={180}
        />
        <Table.Column
          dataIndex="budget_amount"
          title="Valor do Orçamento"
          render={(value) => (
            <TextField
              value={Number(value).toLocaleString("pt-BR", {
                style: "currency",
                currency: "BRL",
              })}
            />
          )}
          width={180}
        />
      </Table>
    </List>
  );
};
